<script setup lang="ts">
// Multi-pick dropdown filter. A trigger button summarises the current
// selection ("Tags (3)" or the placeholder when empty); clicking opens
// a popover containing one checkbox per option. The new selection is
// emitted on every toggle so the host re-runs against the latest
// filter state.
//
// The option list is a `popover="auto"` element in the top layer —
// an absolutely-positioned dropdown was clipped by accordions, the
// drilldown modal's scroll body and customer cards, and ran off the
// right edge of a phone. It is anchored under (or above) the trigger
// and clamped to the viewport; on small screens it becomes a bottom
// sheet instead. Light-dismiss and Esc come with the Popover API.
// Browsers without it keep the old in-flow dropdown with document
// click / Esc listeners.

import { computed, nextTick, onBeforeUnmount, ref, useId, watch } from "vue";

import type { MultiselectOpts, SelectOption } from "../sbi/protocol";
import { useMediaQuery } from "../sbi/embed-width.ts";
import { usePrintMode } from "../sbi/print-mode.ts";
import { useAnchoredPopover } from "./anchored-popover";
import PrintStaticControl from "./PrintStaticControl.vue";
import ControlLabel from "./ControlLabel.vue";

const props = defineProps<{
  id: string;
  value: ReadonlyArray<string | number>;
  options: ReadonlyArray<SelectOption<string | number>>;
  opts?: MultiselectOpts;
}>();

const emit = defineEmits<{
  (
    e: "change-target",
    id: string,
    value: Array<string | number>,
  ): void;
}>();

const isPrint = usePrintMode();

// Label wiring: ControlLabel puts `labelId` on the label text.
const labelId = useId();

const root = ref<HTMLElement | null>(null);
const triggerEl = ref<HTMLElement | null>(null);
const popEl = ref<HTMLElement | null>(null);
const popId = useId();
const summaryId = useId();

// Sheet layout is a viewport decision on purpose: the sheet covers the
// screen, not the embed.
const asSheet = useMediaQuery("(max-width: 639px)");
const {
  open: popoverOpen,
  supported,
  reposition,
  hide,
  // 240px, the same cap the in-flow fallback keeps: a long option list
  // scrolls inside the dropdown rather than covering the page.
} = useAnchoredPopover(triggerEl, popEl, { sheet: () => asSheet.value, maxHeight: 240 });

const fallbackOpen = ref(false);
const open = computed(() => (supported ? popoverOpen.value : fallbackOpen.value));

// Options can arrive or change while open (dynamic source rerun).
watch(
  () => props.options.length,
  () => void nextTick(reposition),
);
watch(asSheet, () => void nextTick(reposition));

function onTriggerClick(): void {
  // With the Popover API the trigger's `popovertarget` toggles.
  if (!supported) fallbackOpen.value = !fallbackOpen.value;
}

const selected = computed(() => new Set(props.value.map((v) => String(v))));

// Print-mode display value: comma-joined labels for the selected
// options. Falls back to the raw values when an option is missing
// (shouldn't happen in practice — option lists and values come from
// the same worker pass). Empty selection renders "(none)" so the
// reader knows the filter is intentionally empty.
const printValue = computed(() => {
  if (props.value.length === 0) return "(none)";
  return props.value
    .map((v) => {
      const match = props.options.find(
        (o) => String(o.value) === String(v),
      );
      return match?.label ?? String(v);
    })
    .join(", ");
});

const summary = computed(() => {
  // Empty options (dynamic source returned zero rows) — surface that
  // explicitly so the trigger reads as a disabled "nothing to pick"
  // instead of an interactive "Select…" prompt that opens an empty
  // popover.
  if (props.options.length === 0) return props.opts?.placeholder ?? "No options";
  const n = props.value.length;
  if (n === 0) return props.opts?.placeholder ?? "Select…";
  if (n === 1) {
    const only = props.options.find(
      (o) => String(o.value) === String(props.value[0]),
    );
    return only?.label ?? String(props.value[0]);
  }
  return `${n} selected`;
});

function toggle(option: SelectOption<string | number>, checked: boolean): void {
  const next = new Set(props.value);
  if (checked) next.add(option.value);
  else next.delete(option.value);
  const ordered = props.options
    .filter((o) => next.has(o.value))
    .map((o) => o.value);
  emit("change-target", props.id, ordered);
}

function onDocClick(e: MouseEvent): void {
  if (!root.value) return;
  // The embed renders inside a shadow root (`<lucent-insight>` uses
  // `shadowRoot: true`). For composed events that bubble to `document`,
  // `e.target` is retargeted to the shadow host — which lives OUTSIDE
  // `root.value` — so `root.contains(e.target)` is always false and the
  // popover would close on every interaction inside it. `composedPath()`
  // sees through the shadow boundary and contains the real click target.
  if (!e.composedPath().includes(root.value)) fallbackOpen.value = false;
}

function onKey(e: KeyboardEvent): void {
  if (e.key === "Escape") fallbackOpen.value = false;
}

// Fallback path only — the Popover API handles both natively.
watch(fallbackOpen, (next) => {
  if (next) {
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
  } else {
    document.removeEventListener("mousedown", onDocClick);
    document.removeEventListener("keydown", onKey);
  }
});

onBeforeUnmount(() => {
  document.removeEventListener("mousedown", onDocClick);
  document.removeEventListener("keydown", onKey);
});
</script>

<template>
  <PrintStaticControl
    v-if="isPrint()"
    :label="opts?.label"
    :value="printValue"
  />
  <div v-else ref="root" class="multiselect">
    <ControlLabel
      v-if="opts?.label || opts?.description"
      class="multiselect-label"
      :label="opts?.label"
      :description="opts?.description"
      :label-id="labelId"
    />
    <button
      ref="triggerEl"
      type="button"
      class="trigger"
      :aria-expanded="open"
      aria-haspopup="listbox"
      :aria-controls="popId"
      :aria-labelledby="opts?.label ? `${labelId} ${summaryId}` : undefined"
      :disabled="options.length === 0"
      :popovertarget="supported ? popId : undefined"
      @click="onTriggerClick"
    >
      <span :id="summaryId" class="summary">{{ summary }}</span>
      <span class="caret" aria-hidden="true">▾</span>
    </button>
    <!--
      Always mounted with the Popover API (the UA hides it until open)
      so the toggle listener is attached before the first open.
    -->
    <div
      v-if="supported || (fallbackOpen && options.length > 0)"
      :id="popId"
      ref="popEl"
      :popover="supported ? 'auto' : undefined"
      class="popover"
      :class="{ 'popover--sheet': supported && asSheet, 'popover--fallback': !supported }"
      role="listbox"
      aria-multiselectable="true"
    >
      <div v-if="supported && asSheet" class="popover-head">
        <span class="popover-title">{{ opts?.label ?? "Select" }}</span>
        <button type="button" class="popover-done" @click="hide">Done</button>
      </div>
      <label
        v-for="o in options"
        :key="String(o.value)"
        class="option"
      >
        <input
          type="checkbox"
          :checked="selected.has(String(o.value))"
          @change="(ev) => toggle(o, (ev.target as HTMLInputElement).checked)"
        />
        <span>{{ o.label }}</span>
      </label>
    </div>
  </div>
</template>

<style scoped>
.multiselect {
  position: relative;
  display: inline-flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
}
@container (max-width: 479px) {
  .multiselect {
    display: flex;
    width: 100%;
  }
}
.multiselect-label {
  color: var(--color-text-muted);
  font-size: 12px;
}
.trigger {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: var(--card);
  color: var(--card-ink);
  border: 1px solid var(--rule);
  border-radius: var(--radius);
  padding: 6px 10px;
  font: inherit;
  cursor: pointer;
  min-width: min(14ch, 100%);
  max-width: 100%;
  touch-action: manipulation;
}
.trigger:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: 1px;
}
.summary {
  flex: 1;
  min-width: 0;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.caret {
  color: var(--color-text-muted);
  font-size: 10px;
}
.popover {
  flex-direction: column;
  gap: 2px;
  background: var(--card);
  color: var(--card-ink);
  border: 1px solid var(--rule);
  border-radius: var(--radius);
  padding: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  overflow-y: auto;
  overscroll-behavior: contain;
  box-sizing: border-box;
}
/* Top-layer dropdown, placed by useAnchoredPopover. `display` only on
   the open state so the UA keeps it hidden when closed. */
.popover:popover-open {
  display: flex;
  position: fixed;
  inset: auto;
  margin: 0;
  width: max-content;
  min-width: max(var(--anchor-w, 0px), 12rem);
  max-width: min(22rem, calc(100vw - 16px));
}
/* Small screens: a bottom sheet. */
.popover--sheet:popover-open {
  inset: auto 0 0 0;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  max-height: min(70vh, 480px);
  max-height: min(70dvh, 480px);
  border-radius: 16px 16px 0 0;
  padding: 0 8px max(8px, env(safe-area-inset-bottom, 0px));
}
.popover--sheet::backdrop {
  background: rgba(0, 0, 0, 0.35);
}
.popover-head {
  position: sticky;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 4px;
  background: var(--card);
  border-bottom: 1px solid var(--rule-soft, var(--rule));
}
.popover-title {
  font-weight: 600;
  min-width: 0;
  overflow-wrap: anywhere;
}
.popover-done {
  appearance: none;
  min-height: 44px;
  padding: 0 14px;
  border: 0;
  background: transparent;
  color: var(--primary);
  font: inherit;
  font-weight: 600;
  cursor: pointer;
}
/* No Popover API: the original in-flow dropdown. */
.popover--fallback {
  display: flex;
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 10;
  min-width: 100%;
  max-width: min(22rem, calc(100vw - 16px));
  max-height: 240px;
}
.option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  border-radius: calc(var(--radius) - 2px);
  cursor: pointer;
  color: var(--card-ink);
  white-space: normal;
  overflow-wrap: anywhere;
}
.option:hover {
  background: var(--rule);
}
.option input[type="checkbox"] {
  flex: 0 0 auto;
  accent-color: var(--primary);
}
@media (pointer: coarse) {
  .trigger {
    min-height: 44px;
    font-size: 16px;
  }
  .option {
    min-height: 44px;
    padding: 10px 12px;
  }
  .option input[type="checkbox"] {
    width: 20px;
    height: 20px;
  }
}
</style>
